/**
 * Proxy Engine Service — delegates all calls to the currently active engine.
 *
 * Used by Editor, Web, and Play to create a stable EngineService reference
 * that can be passed to setPlatformService() once, while the underlying engine
 * implementation swaps at runtime (stub ↔ HTTP client ↔ Tauri).
 *
 * @param getActive - Returns the currently active EngineService from the connector store.
 * @param subscribe - Optional. Subscribe to engine service changes to automatically
 *                    re-register notification handlers when the service swaps.
 *                    If omitted, listenNotifications simply delegates to the current service.
 */

import type { EngineService, PlatformService } from "../platform";
import { frontendLog } from "../utils/frontend-logger";
import type { SettingsService } from "./settings-service";

export const PLATFORM_CONNECTION_CHANGED_EVENT =
    "magic-md:engine-connector-changed";

export interface ProxyEngineServiceOptions {
    /** Returns the currently active EngineService */
    getActive: () => EngineService;
    /**
     * Subscribe to service changes. Called with the new EngineService whenever
     * the underlying engine swaps (e.g. stub → HTTP client).
     * Returns an unsubscribe function.
     */
    subscribe?: (listener: (newService: EngineService) => void) => () => void;
}

export function createProxyEngineService({
    getActive,
    subscribe,
}: ProxyEngineServiceOptions): EngineService {
    return {
        getEngineStatus: () => getActive().getEngineStatus(),
        initialize: (clientInfo, capabilities) =>
            getActive().initialize(clientInfo, capabilities),
        sessionCreate: (params) => getActive().sessionCreate(params),
        sessionClose: (sessionId) => getActive().sessionClose(sessionId),
        sessionSync: (params) => getActive().sessionSync(params),
        executionStart: (params) => getActive().executionStart(params),
        executionGetState: (params) => getActive().executionGetState(params),
        executionCancel: (params) => getActive().executionCancel(params),
        jobList: (params) =>
            getActive().jobList?.(params) ??
            Promise.reject(new Error("Runtime job API not available")),
        jobGetState: (params) =>
            getActive().jobGetState?.(params) ??
            Promise.reject(new Error("Runtime job API not available")),
        jobCancel: (params) =>
            getActive().jobCancel?.(params) ??
            Promise.reject(new Error("Runtime job API not available")),
        sessionList: () => getActive().sessionList(),
        sessionGetState: (params) => getActive().sessionGetState(params),
        sessionGetOutput: (params) => getActive().sessionGetOutput(params),
        sessionExplain: (params) => getActive().sessionExplain(params),
        sessionWatch: (params) => getActive().sessionWatch(params),
        sessionUnwatch: (params) => getActive().sessionUnwatch(params),
        pluginList: () => getActive().pluginList(),
        pluginApiCall: (params) =>
            getActive().pluginApiCall?.(params) ??
            Promise.reject(new Error("Plugin runtime API not available")),
        smartIntakePropose: (params) =>
            getActive().smartIntakePropose?.(params) ??
            Promise.resolve({ offers: [] }),
        smartIntakeBuildPlan: (params) =>
            getActive().smartIntakeBuildPlan?.(params) ??
            Promise.resolve({ plan: { operations: [] } }),
        configGet: (params) =>
            getActive().configGet?.(params) ??
            Promise.reject(new Error("Config API not available")),
        configSet: (params) =>
            getActive().configSet?.(params) ??
            Promise.reject(new Error("Config API not available")),
        configGetSchema: (params) =>
            getActive().configGetSchema?.(params) ??
            Promise.reject(new Error("Config API not available")),
        configReload: (params) =>
            getActive().configReload?.(params) ??
            Promise.reject(new Error("Config API not available")),
        sidecarList: () => getActive().sidecarList(),
        sidecarShutdown: (id) => getActive().sidecarShutdown(id),
        sidecarSpawn: (id, cmd, args) => getActive().sidecarSpawn(id, cmd, args),
        sidecarCall: (id, method, params) =>
            getActive().sidecarCall(id, method, params),
        sidecarCallStart: (params) =>
            getActive().sidecarCallStart?.(params) ??
            Promise.reject(new Error("Async sidecar call API not available")),

        listenNotifications: subscribe
            ? async (h) => {
                let currentService = getActive();
                let activeUnlisten: (() => void) | null = null;
                let activationGeneration = 0;
                let stopped = false;
                let activationPromise: Promise<void> = Promise.resolve();
                const stopActive = () => {
                    const stop = activeUnlisten;
                    activeUnlisten = null;
                    stop?.();
                };

                const activate = (service: EngineService): Promise<void> => {
                    const generation = ++activationGeneration;
                    stopActive();

                    const pending = service
                        .listenNotifications(h)
                        .then((unlisten) => {
                            if (
                                stopped ||
                                generation !== activationGeneration
                            ) {
                                unlisten();
                                return;
                            }
                            activeUnlisten = unlisten;
                        })
                        .catch((error) => {
                            if (
                                stopped ||
                                generation !== activationGeneration
                            ) {
                                return;
                            }
                            throw error;
                        });
                    activationPromise = pending;
                    return pending;
                };

                // When the engine service changes (e.g. stub → real),
                // re-register the notification handler on the new service
                // so real-time events are not silently dropped.
                const unsub = subscribe((newService) => {
                    if (newService !== currentService) {
                        currentService = newService;
                        void activate(newService).catch((error) => {
                            frontendLog.error(
                                "[EngineProxy] Failed to switch notification transport",
                                error,
                                { source: "engine" },
                            );
                        });
                    }
                });

                try {
                    await activate(currentService);
                    // A service swap can arrive while the first async listener is
                    // opening. Wait until the latest activation owns the handler.
                    for (;;) {
                        const pending = activationPromise;
                        await pending;
                        if (pending === activationPromise) break;
                    }
                } catch (error) {
                    stopped = true;
                    activationGeneration += 1;
                    unsub();
                    stopActive();
                    throw error;
                }

                return () => {
                    if (stopped) return;
                    stopped = true;
                    activationGeneration += 1;
                    unsub();
                    stopActive();
                };
            }
            : (h) => getActive().listenNotifications(h),

        stopListening: () => getActive().stopListening(),

        // ── Push methods ──
        pushScan: (params) =>
            getActive().pushScan?.(params) ??
            Promise.resolve({ devices: [] }),
        pushConnect: (params) =>
            getActive().pushConnect?.(params) ??
            Promise.resolve({ connected: false }),
        pushDisconnect: () =>
            getActive().pushDisconnect?.() ??
            Promise.resolve({ disconnected: false }),
        pushPair: (params) =>
            getActive().pushPair?.(params) ??
            Promise.resolve({ success: false }),
        pushSend: (params) =>
            getActive().pushSend?.(params) ??
            Promise.resolve({ received: false }),
        pushSyncStart: (params) =>
            getActive().pushSyncStart?.(params) ??
            Promise.resolve({ started: false }),
        pushSyncStop: () =>
            getActive().pushSyncStop?.() ??
            Promise.resolve({ stopped: false }),
        pushStatus: () =>
            getActive().pushStatus?.() ??
            Promise.resolve({
                connected: false,
                paired: false,
                syncing: false,
            }),

        // ── MCP methods ──
        mcpStart: (params) =>
            getActive().mcpStart?.(params) ??
            Promise.reject(new Error("MCP not available")),
        mcpStop: () =>
            getActive().mcpStop?.() ??
            Promise.reject(new Error("MCP not available")),
        mcpStatus: () =>
            getActive().mcpStatus?.() ??
            Promise.resolve({
                status: "stopped" as const,
                endpoint: null,
                port: null,
                connectedClients: 0,
                clients: [],
            }),
        mcpLogs: (params) =>
            getActive().mcpLogs?.(params) ??
            Promise.resolve({ logs: [], total: 0 }),

        // ── Extension methods ──
        extensionList: () =>
            getActive().extensionList?.() ??
            Promise.resolve({ extensions: [] }),
        extensionGetManifest: (params) =>
            getActive().extensionGetManifest?.(params) ??
            Promise.reject(new Error("Extension API not available")),
        extensionActivateRuntime: (params) =>
            getActive().extensionActivateRuntime?.(params) ??
            Promise.resolve({
                extensionId: params.extensionId,
                activated: false,
            }),
        extensionInvoke: (params) =>
            getActive().extensionInvoke?.(params) ??
            Promise.resolve({
                success: false,
                message: "Extension runtime not available",
            }),
        extensionDisposeRuntime: (params) =>
            getActive().extensionDisposeRuntime?.(params) ??
            Promise.resolve({
                extensionId: params.extensionId,
                disposed: false,
            }),
        extensionSettingsUpdate: (params) =>
            getActive().extensionSettingsUpdate?.(params) ??
            Promise.resolve({
                extensionId: params.extensionId,
                applied: false,
            }),

        // ── Search methods ──
        searchQuery: (params) =>
            getActive().searchQuery?.(params) ??
            Promise.resolve({ searchId: "" }),
        searchCancel: (params) =>
            getActive().searchCancel?.(params) ??
            Promise.resolve({ success: false }),

        // ── Intelligence methods ──
        intelligenceComplete: (params) =>
            getActive().intelligenceComplete?.(params) ??
            Promise.resolve({ items: [] }),
        intelligenceHover: (params) =>
            getActive().intelligenceHover?.(params) ?? Promise.resolve(null),
        intelligenceDiagnostics: (params) =>
            getActive().intelligenceDiagnostics?.(params) ??
            Promise.resolve({ diagnostics: [] }),
        intelligenceDefinition: (params) =>
            getActive().intelligenceDefinition?.(params) ?? Promise.resolve(null),
        intelligenceReferences: (params) =>
            getActive().intelligenceReferences?.(params) ??
            Promise.resolve({ references: [] }),
        intelligenceRename: (params) =>
            getActive().intelligenceRename?.(params) ??
            Promise.resolve({ newContent: "", changes: 0 }),
        intelligenceSymbols: (params) =>
            getActive().intelligenceSymbols?.(params) ??
            Promise.resolve({ symbols: [] }),
        intelligenceSemanticTokens: (params) =>
            getActive().intelligenceSemanticTokens?.(params) ??
            Promise.resolve({ tokens: [] }),
        intelligenceCodeAction: (params) =>
            getActive().intelligenceCodeAction?.(params) ??
            Promise.resolve({ actions: [] }),
        intelligenceInlayHint: (params) =>
            getActive().intelligenceInlayHint?.(params) ??
            Promise.resolve({ hints: [] }),

        // ── Workspace methods ──
        workspaceReadDir: (params) => getActive().workspaceReadDir(params),
        workspaceReadFile: (params) => getActive().workspaceReadFile(params),
        workspaceWriteFile: (params) => getActive().workspaceWriteFile(params),
        workspaceStat: (params) => getActive().workspaceStat(params),
        workspaceExists: (params) => getActive().workspaceExists(params),
        workspaceMkdir: (params) => getActive().workspaceMkdir(params),
        workspaceRename: (params) => getActive().workspaceRename(params),
        workspaceRemove: (params) => getActive().workspaceRemove(params),
        workspaceCopy: (params) => getActive().workspaceCopy(params),
        workspaceMove: (params) => getActive().workspaceMove(params),
        workspaceReferenceImpact: (params) => getActive().workspaceReferenceImpact(params),
        workspaceGetInfo: () => getActive().workspaceGetInfo(),
        workspaceAddFolder: (params) => getActive().workspaceAddFolder(params),
        workspaceRemoveFolder: (params) => getActive().workspaceRemoveFolder(params),
        workspaceListFolders: () => getActive().workspaceListFolders(),
        workspaceSave: (params) => getActive().workspaceSave(params),
        workspaceLoad: (params) => getActive().workspaceLoad(params),
        workspaceStarList: () => getActive().workspaceStarList(),
        workspaceStarAdd: (params) => getActive().workspaceStarAdd(params),
        workspaceStarRemove: (params) => getActive().workspaceStarRemove(params),
        workspaceStarUpdate: (params) => getActive().workspaceStarUpdate(params),
        workspaceBookmarkList: () => getActive().workspaceBookmarkList(),
        workspaceBookmarkAdd: (params) => getActive().workspaceBookmarkAdd(params),
        workspaceBookmarkRemove: (params) => getActive().workspaceBookmarkRemove(params),
        workspaceBookmarkUpdate: (params) => getActive().workspaceBookmarkUpdate(params),
        workspaceSessionGet: () => getActive().workspaceSessionGet(),
        workspaceSessionSet: (params) => getActive().workspaceSessionSet(params),

        // ── Publish methods ──
        publishStart: (params) => getActive().publishStart(params),
        publishGetState: (params) => getActive().publishGetState(params),
        publishCancel: (params) => getActive().publishCancel(params),
        publishSiteStart: (params) => getActive().publishSiteStart(params),
        publishSiteGetState: (params) => getActive().publishSiteGetState(params),
        publishSiteCancel: (params) => getActive().publishSiteCancel(params),

        // ── History methods ──
        historySnapshot: (params) => getActive().historySnapshot(params),
        historyList: (params) => getActive().historyList(params),
        historyGet: (params) => getActive().historyGet(params),
        historyDiff: (params) => getActive().historyDiff(params),
        historyRestore: (params) => getActive().historyRestore(params),
        historyDelete: (params) => getActive().historyDelete(params),
        historyTag: (params) => getActive().historyTag(params),
        historyPrune: (params) => getActive().historyPrune(params),
    };
}

/* ── Convenience: create PlatformService from a Zustand store ── */

/**
 * Create a PlatformService whose engine delegates to the `engineService`
 * field of a Zustand store. All three apps (Editor, Web, Play) use exactly
 * this pattern, so this helper eliminates the per-app boilerplate.
 *
 * @example
 * ```ts
 * const platform = createPlatformServiceFromStore(useEngineConnectorStore);
 * setPlatformService(platform);
 * ```
 */
export function createPlatformServiceFromStore<
    S extends { engineService: EngineService },
>(store: {
    getState: () => S;
    subscribe: (listener: (state: S) => void) => () => void;
}, options: {
    openExternal?: (url: string) => Promise<void>;
    getAssetUrl?: (filePath: string) => string | undefined;
    settings: SettingsService;
    openExtensionFloatingPanel?: PlatformService["openExtensionFloatingPanel"];
    closeExtensionFloatingPanel?: PlatformService["closeExtensionFloatingPanel"];
    openExtensionSidebarPanel?: PlatformService["openExtensionSidebarPanel"];
    supportsExtensionPanelWindows?: PlatformService["supportsExtensionPanelWindows"];
    canApplyConfigEffectNow?: PlatformService["canApplyConfigEffectNow"];
    applyConfigEffectNow?: PlatformService["applyConfigEffectNow"];
}): PlatformService {
    if (typeof window !== "undefined") {
        let previousState = store.getState() as S & {
            mode?: string;
            connected?: boolean;
        };
        store.subscribe((state) => {
            const nextState = state as S & {
                mode?: string;
                connected?: boolean;
            };
            const engineChanged = nextState.engineService !== previousState.engineService;
            const modeChanged = nextState.mode !== previousState.mode;
            const connectedChanged = nextState.connected !== previousState.connected;

            if (engineChanged || modeChanged || connectedChanged) {
                window.dispatchEvent(
                    new CustomEvent(PLATFORM_CONNECTION_CHANGED_EVENT, {
                        detail: {
                            mode: nextState.mode ?? null,
                            connected: nextState.connected ?? null,
                        },
                    }),
                );
            }

            previousState = nextState;
        });
    }

    return {
        engine: createProxyEngineService({
            getActive: () => store.getState().engineService,
            subscribe: (listener) =>
                store.subscribe((state) => listener(state.engineService)),
        }),
        settings: options.settings,
        openExternal: options?.openExternal,
        getAssetUrl: options?.getAssetUrl,
        openExtensionFloatingPanel: options?.openExtensionFloatingPanel,
        closeExtensionFloatingPanel: options?.closeExtensionFloatingPanel,
        openExtensionSidebarPanel: options?.openExtensionSidebarPanel,
        supportsExtensionPanelWindows: options?.supportsExtensionPanelWindows,
        canApplyConfigEffectNow: options?.canApplyConfigEffectNow,
        applyConfigEffectNow: options?.applyConfigEffectNow,
    };
}
